import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Button, InformationalScreen, LoadingSpinner, Stack, Text, } from '@adc/parallax-component-library';
import axios from 'axios';
import Document from 'Components/consents/Document';
import { MainContainer, RichText } from 'Components/utility';
import { CLOSE_MODAL, SHOW_MODAL } from 'Reducers/modal/types';
import { notifyApp } from 'Utilities/appCommunicator';
import { getDocumentPathFromLanguage } from 'Utilities/dataHelpers';
import i18n from 'Utilities/i18n';
import mediator from 'Utilities/mediator';
import { getSessionObject, removeSessionObject, setSessionObject } from 'Utilities/storage';
import { store } from 'Utilities/store';
import { handleConsent } from 'Services/consent';
const PageIndexes = {
    TERMS_OF_USE: 0,
    PRIVACY_NOTICE: 1,
    HIPAA: 2,
    RWE: 3,
};
const accepted = new Set([]);
const declined = new Set([]);
const mapStateToProps = ({ env: { osType, osVersion, app, appVersion, phoneLanguage }, auth, config: { documentUrls, documentPaths }, }) => ({
    osType,
    osVersion,
    app,
    appVersion,
    consents: auth.include.patient.consents,
    accountId: auth.account_id,
    cmsDomain: documentUrls.cms,
    hipaaCmsPaths: documentPaths.cms?.hipaalibre,
    rweCmsPaths: documentPaths.cms?.rwelibre,
    phoneLanguage,
});
const connector = connect(mapStateToProps);
const NoticesConsents = ({ osType, osVersion, app, appVersion, consents: { touLibre, pp, hipaa, realWorldEvidence }, accountId, cmsDomain, hipaaCmsPaths, rweCmsPaths, phoneLanguage, }) => {
    const [loading, setLoading] = useState(true);
    const [currentPageIndex, setCurrentPageIndex] = useState(PageIndexes.TERMS_OF_USE);
    const [hipaaBodyText, setHipaaBodyText] = useState(i18n.t('Hipaa.content.hipaaRichText.bodyText'));
    const [rweBodyText, setRweBodyText] = useState(i18n.t('Research.content.researchRichText.bodyText'));
    const isConsentOutdated = useCallback((consent) => consent.at < consent.latestVersion, []);
    const dispatchModal = useCallback((details, testID) => {
        store.dispatch({
            type: SHOW_MODAL,
            modalProps: { details, testID },
        });
    }, []);
    const handleError = useCallback((error) => {
        notifyApp('adc-webview:notices-consents', {
            error: { message: error.message, status: error.status },
        });
    }, []);
    const verifyConsents = useCallback(async () => {
        try {
            if (isConsentOutdated(hipaa) || isConsentOutdated(realWorldEvidence)) {
                const hipaaCmsUrl = getDocumentPathFromLanguage(hipaaCmsPaths, phoneLanguage);
                const rweCmsUrl = getDocumentPathFromLanguage(rweCmsPaths, phoneLanguage);
                const hipaaCmsFullDomain = `${cmsDomain}${hipaaCmsUrl}`;
                const rweCmsFullDomain = `${cmsDomain}${rweCmsUrl}`;
                const { data: hipaaData } = await axios.get(hipaaCmsFullDomain);
                const { data: rweData } = await axios.get(rweCmsFullDomain);
                setRweBodyText(rweData.Research.content.researchRichText.bodyText);
                setHipaaBodyText(hipaaData.Hipaa.content.hipaaRichText.bodyText);
                setCurrentPageIndex(isConsentOutdated(hipaa) ? PageIndexes.HIPAA : PageIndexes.RWE);
                dispatchModal(i18n.t('Consents.modals.reviewConsent.body'), 'ReviewConsent');
                setLoading(false);
                return;
            }
            notifyApp('adc-webview:notices-consents', { declined, accepted });
        }
        catch (err) {
            const error = err;
            handleError(error);
        }
    }, [
        isConsentOutdated,
        hipaa,
        realWorldEvidence,
        hipaaCmsPaths,
        phoneLanguage,
        rweCmsPaths,
        cmsDomain,
        dispatchModal,
        handleError,
    ]);
    const verifyNotices = useCallback(() => {
        const currentPageIndexSession = getSessionObject('currentPageIndex');
        if (currentPageIndexSession) {
            setCurrentPageIndex(currentPageIndexSession);
            removeSessionObject('currentPageIndex');
            setLoading(false);
            return;
        }
        if (isConsentOutdated(touLibre) || isConsentOutdated(pp)) {
            setCurrentPageIndex(isConsentOutdated(touLibre) ? PageIndexes.TERMS_OF_USE : PageIndexes.PRIVACY_NOTICE);
            dispatchModal(i18n.t('Consents.modals.reviewLegalNotice.body'), 'ReviewLegalNotice');
            setLoading(false);
            return;
        }
        verifyConsents();
    }, [dispatchModal, isConsentOutdated, pp, touLibre, verifyConsents]);
    // Init
    useEffect(() => {
        verifyNotices();
    }, [verifyNotices]);
    const addAcceptedConsent = useCallback((consent) => {
        accepted.add(consent);
        declined.delete(consent);
    }, []);
    const addDeclinedConsent = useCallback((consent) => {
        declined.add(consent);
        accepted.delete(consent);
    }, []);
    const displayNoticeDeclineModal = useCallback((id) => {
        store.dispatch({
            type: SHOW_MODAL,
            modalProps: {
                details: (React.createElement(Stack, { gap: "$4" },
                    React.createElement(Text, { color: "$text.100", testID: "Consents.modals.acceptLegalNotice.title", fontSize: "$bodyBase.strong", fontWeight: "$bodyBase.strong" }, i18n.t('Consents.modals.acceptLegalNotice.title')),
                    React.createElement(Text, { testID: "Consents.modals.acceptLegalNotice.body" }, i18n.t('Consents.modals.acceptLegalNotice.body')))),
                buttonLayout: 'column',
                primaryAction: {
                    destructive: true,
                    label: i18n.t('Consents.modals.acceptLegalNotice.declineAnyway'),
                    onPress: () => {
                        addDeclinedConsent(id);
                        notifyApp('adc-webview:notices-consents', {
                            declined,
                            accepted,
                        });
                        store.dispatch({ type: CLOSE_MODAL });
                    },
                },
                secondaryAction: {
                    label: 'Back',
                    onPress: () => store.dispatch({ type: CLOSE_MODAL }),
                },
                testID: 'AcceptLegalNotice',
            },
        });
    }, [addDeclinedConsent]);
    const updateConsent = useCallback(async (id, action) => {
        setLoading(true);
        await handleConsent(accountId, action, osType, osVersion, app, appVersion, id);
        setLoading(false);
    }, [accountId, app, appVersion, osType, osVersion]);
    const handleAccept = useCallback(async () => {
        switch (currentPageIndex) {
            case PageIndexes.TERMS_OF_USE: {
                try {
                    await updateConsent('touLibre', 'accept');
                    addAcceptedConsent('touLibre');
                    if (isConsentOutdated(pp)) {
                        setCurrentPageIndex(PageIndexes.PRIVACY_NOTICE);
                    }
                    else {
                        verifyConsents();
                    }
                }
                catch (err) {
                    const error = err;
                    handleError(error);
                }
                break;
            }
            case PageIndexes.PRIVACY_NOTICE: {
                try {
                    await updateConsent('pp', 'accept');
                    addAcceptedConsent('pp');
                    verifyConsents();
                }
                catch (err) {
                    const error = err;
                    handleError(error);
                }
                break;
            }
            case PageIndexes.HIPAA: {
                try {
                    await updateConsent('hipaa', 'accept');
                    addAcceptedConsent('hipaa');
                    if (isConsentOutdated(realWorldEvidence)) {
                        setCurrentPageIndex(PageIndexes.RWE);
                    }
                    else {
                        notifyApp('adc-webview:notices-consents', {
                            declined,
                            accepted,
                        });
                    }
                }
                catch (err) {
                    const error = err;
                    handleError(error);
                }
                break;
            }
            case PageIndexes.RWE: {
                try {
                    await updateConsent('realWorldEvidence', 'accept');
                    addAcceptedConsent('realWorldEvidence');
                    notifyApp('adc-webview:notices-consents', {
                        declined,
                        accepted,
                    });
                }
                catch (err) {
                    const error = err;
                    handleError(error);
                }
                break;
            }
        }
    }, [
        addAcceptedConsent,
        currentPageIndex,
        handleError,
        isConsentOutdated,
        pp,
        realWorldEvidence,
        updateConsent,
        verifyConsents,
    ]);
    const handleDecline = useCallback(async () => {
        switch (currentPageIndex) {
            case PageIndexes.TERMS_OF_USE:
                displayNoticeDeclineModal('touLibre');
                break;
            case PageIndexes.PRIVACY_NOTICE:
                displayNoticeDeclineModal('pp');
                break;
            case PageIndexes.HIPAA: {
                try {
                    await updateConsent('hipaa', 'decline');
                    addDeclinedConsent('hipaa');
                    if (isConsentOutdated(realWorldEvidence)) {
                        setCurrentPageIndex(PageIndexes.RWE);
                    }
                    else {
                        notifyApp('adc-webview:notices-consents', {
                            declined,
                            accepted,
                        });
                    }
                }
                catch (err) {
                    const error = err;
                    handleError(error);
                }
                break;
            }
            case PageIndexes.RWE: {
                try {
                    await updateConsent('realWorldEvidence', 'decline');
                    addDeclinedConsent('realWorldEvidence');
                    notifyApp('adc-webview:notices-consents', {
                        declined,
                        accepted,
                    });
                }
                catch (err) {
                    const error = err;
                    handleError(error);
                }
                break;
            }
            default:
                break;
        }
    }, [
        addDeclinedConsent,
        currentPageIndex,
        displayNoticeDeclineModal,
        handleError,
        isConsentOutdated,
        realWorldEvidence,
        updateConsent,
    ]);
    const pageTitle = useMemo(() => {
        switch (currentPageIndex) {
            case PageIndexes.TERMS_OF_USE:
                return i18n.t('Global.microcopy.common.termsOfUse');
            case PageIndexes.PRIVACY_NOTICE:
                return i18n.t('Global.microcopy.common.privacyNotice');
            case PageIndexes.HIPAA:
                return i18n.t('Hipaa.title');
            case PageIndexes.RWE:
                return i18n.t('Research.title');
            default:
                return '';
        }
    }, [currentPageIndex]);
    if (loading) {
        return (React.createElement(MainContainer, null,
            React.createElement(Stack, { flex: 1, justifyContent: "center", alignItems: "center" },
                React.createElement(LoadingSpinner, { "aria-label": "Loading" }))));
    }
    return (React.createElement(MainContainer, null,
        React.createElement(InformationalScreen, { topNavigationProps: {
                title: pageTitle,
            }, testID: "NoticesConsents.title", index: currentPageIndex },
            React.createElement(InformationalScreen.Page, null,
                React.createElement(InformationalScreen.Content, null,
                    React.createElement(Document, { type: "termsOfUse", hideHeader: true })),
                React.createElement(InformationalScreen.Footer, null,
                    React.createElement(Stack, { alignSelf: "flex-end", width: "100%", gap: "$4" },
                        React.createElement(Button, { onPress: handleAccept, testID: "TermsOfUse.accept", label: i18n.t('Global.microcopy.common.accept') }),
                        React.createElement(Button, { variant: "tertiary", onPress: handleDecline, label: i18n.t('Global.microcopy.common.decline'), testID: "TermsOfUse.decline" })))),
            React.createElement(InformationalScreen.Page, null,
                React.createElement(InformationalScreen.Content, null,
                    React.createElement(Document, { type: "privacyNotice", hideHeader: true })),
                React.createElement(InformationalScreen.Footer, null,
                    React.createElement(Stack, { alignSelf: "flex-end", width: "100%", gap: "$4" },
                        React.createElement(Button, { onPress: handleAccept, testID: "PrivacyNotice.accept", label: i18n.t('Global.microcopy.common.accept') }),
                        React.createElement(Button, { variant: "tertiary", onPress: handleDecline, label: i18n.t('Global.microcopy.common.decline'), testID: "PrivacyNotice.decline" })))),
            React.createElement(InformationalScreen.Page, null,
                React.createElement(InformationalScreen.Content, null,
                    React.createElement(RichText, { testID: "Hipaa.content.hipaaRichText.bodyText", text: hipaaBodyText })),
                React.createElement(InformationalScreen.Footer, null,
                    React.createElement(Stack, { alignSelf: "flex-end", width: "100%", gap: "$4" },
                        React.createElement(Button, { onPress: handleAccept, testID: "Hipaa.accept", label: i18n.t('Global.microcopy.common.accept') }),
                        React.createElement(Button, { variant: "tertiary", onPress: handleDecline, label: i18n.t('Global.microcopy.common.decline'), testID: "Hipaa.decline" })))),
            React.createElement(InformationalScreen.Page, null,
                React.createElement(InformationalScreen.Content, null,
                    React.createElement(RichText, { testID: "Research.content.researchRichText.bodyText", text: rweBodyText, onPressLink: (href) => {
                            // Save the current page index in the session so the user can return to the same page
                            setSessionObject('currentPageIndex', PageIndexes.RWE);
                            mediator.publish('router:navigate', href);
                        } })),
                React.createElement(InformationalScreen.Footer, null,
                    React.createElement(Stack, { alignSelf: "flex-end", width: "100%", gap: "$4" },
                        React.createElement(Button, { onPress: handleAccept, testID: "RWE.optIn", label: i18n.t('Global.microcopy.common.optIn'), "aria-label": i18n.t('Global.microcopy.common.optIn') }),
                        React.createElement(Button, { variant: "tertiary", onPress: handleDecline, label: i18n.t('Global.microcopy.common.decline'), "aria-label": i18n.t('Global.microcopy.common.decline'), testID: "RWE.decline" })))))));
};
export default connector(NoticesConsents);
